import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { 
  Crown, Building2, Users, CreditCard, 
  Bell, History, LogOut, Loader2,
  Plus, Edit, Trash2, Search, Eye,
  Play, Pause, Ban, RefreshCw, UserCheck,
  Settings, Calendar, Download, AlertTriangle,
  Mail, Clock, ChevronRight, User, Briefcase,
  Shield, Database, Globe, Key, Server, Zap,
  TrendingUp, TrendingDown, DollarSign, Activity, BarChart3,
  Check, Image, Upload, X, FileText
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import QuoteAdminPanel from "../components/QuoteAdminPanel";

const ADMIN_API = process.env.REACT_APP_BACKEND_URL + '/api';

const SaaSAdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [impersonationLogs, setImpersonationLogs] = useState([]);
  
  // Filters
  const [companyFilter, setCompanyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [logActionFilter, setLogActionFilter] = useState("");
  const [notificationTypeFilter, setNotificationTypeFilter] = useState("");
  const [showReadNotifications, setShowReadNotifications] = useState(true);
  
  // Dialogs
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showEditCompanyDialog, setShowEditCompanyDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showEmployeesDialog, setShowEmployeesDialog] = useState(false);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);
  
  // Company Edit Form
  const [companyForm, setCompanyForm] = useState({
    name: "", code: "", plan_id: "", billing_email: "", status: "",
    address: "", phone: "", max_employees: 0, max_admins: 0,
    trial_ends_at: "", subscription_ends_at: ""
  });
  
  // New Company Form
  const [newCompanyForm, setNewCompanyForm] = useState({
    name: "", billing_email: "", plan_id: "", admin_name: "", admin_email: "", admin_password: "", admin_role: "admin"
  });
  
  // User Edit Form
  const [userForm, setUserForm] = useState({
    name: "", email: "", password: "", role: "admin", is_active: true
  });
  
  // Company Details Data
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [companyAttendance, setCompanyAttendance] = useState([]);
  
  const [planForm, setPlanForm] = useState({
    name: "", max_users: 3, max_employees: 10, price_monthly: 0, price_yearly: 0,
    features: { face_auth: true, csv_export: false, print: false, email_notify: false, api_access: false, multi_admin: false },
    sort_order: 0
  });
  
  const [saving, setSaving] = useState(false);
  
  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    trial_days: 14,
    allow_registration: true,
    require_email_verification: false,
    default_plan_id: "",
    company_code_prefix: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_from_name: "PRC勤怠管理",
    contact_notification_email: "",
    maintenance_mode: false,
    maintenance_message: "",
    max_login_attempts: 5,
    session_timeout_hours: 24,
    password_min_length: 8,
    require_strong_password: true
  });

  // Operating Company Info
  const [operatingCompany, setOperatingCompany] = useState({
    company_name: "",
    company_name_kana: "",
    representative_name: "",
    postal_code: "",
    address: "",
    phone: "",
    fax: "",
    email: "",
    website: "",
    invoice_number: "",
    bank_name: "",
    bank_branch: "",
    bank_account_type: "普通",
    bank_account_number: "",
    bank_account_holder: ""
  });

  // Logo settings
  const [logoUrl, setLogoUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const featureLabels = {
    face_auth: "顔認証",
    csv_export: "CSVエクスポート",
    print: "印刷機能",
    email_notify: "メール通知",
    api_access: "API連携",
    multi_admin: "複数管理者"
  };

  const statusLabels = {
    trial: "トライアル",
    active: "有効",
    suspended: "停止中",
    cancelled: "解約"
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/saas-admin/login");
      return;
    }
    fetchAdminData();
  }, [navigate]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`
  });

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      const [adminRes, companiesRes, plansRes, metricsRes, logsRes, notifRes, settingsRes, opCompanyRes, impLogsRes] = await Promise.all([
        axios.get(`${ADMIN_API}/admin/me`, { headers }).catch(() => null),
        axios.get(`${ADMIN_API}/admin/companies`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${ADMIN_API}/admin/plans`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${ADMIN_API}/admin/metrics/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${ADMIN_API}/admin/logs?limit=100`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${ADMIN_API}/admin/notifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${ADMIN_API}/admin/settings`, { headers }).catch(() => ({ data: null })),
        axios.get(`${ADMIN_API}/admin/operating-company`, { headers }).catch(() => ({ data: null })),
        axios.get(`${ADMIN_API}/admin/impersonation-logs?limit=100`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      if (!adminRes) {
        localStorage.removeItem("admin_token");
        navigate("/saas-admin/login");
        return;
      }
      
      setAdmin(adminRes.data);
      setCompanies(companiesRes.data || []);
      setPlans(plansRes.data || []);
      setMetrics(metricsRes.data);
      setLogs(logsRes.data || []);
      setNotifications(notifRes.data || []);
      setImpersonationLogs(impLogsRes.data || []);
      if (settingsRes.data) {
        setSystemSettings(prev => ({ ...prev, ...settingsRes.data }));
        // ロゴURLを取得
        if (settingsRes.data.logo_url) {
          setLogoUrl(settingsRes.data.logo_url);
        }
      }
      if (opCompanyRes.data) {
        setOperatingCompany(prev => ({ ...prev, ...opCompanyRes.data }));
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("画像ファイルを選択してください");
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("画像サイズは2MB以下にしてください");
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        try {
          const headers = getAuthHeaders();
          await axios.post(`${ADMIN_API}/admin/settings/logo`, { logo: base64 }, { headers });
          setLogoUrl(base64);
          toast.success("ロゴをアップロードしました");
        } catch (err) {
          toast.error(err.response?.data?.detail || "ロゴのアップロードに失敗しました");
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("ファイルの読み込みに失敗しました");
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm("ロゴを削除しますか？")) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${ADMIN_API}/admin/settings/logo`, { headers });
      setLogoUrl("");
      toast.success("ロゴを削除しました");
    } catch (error) {
      toast.error("ロゴの削除に失敗しました");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/saas-admin/login");
  };

  // Company Actions
  const viewCompany = async (company) => {
    setSelectedCompany(company);
    setShowCompanyDialog(true);
    
    // Fetch company details
    try {
      const headers = getAuthHeaders();
      const [usersRes, employeesRes] = await Promise.all([
        axios.get(`${ADMIN_API}/admin/companies/${company.id}/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${ADMIN_API}/admin/companies/${company.id}/employees`, { headers }).catch(() => ({ data: [] }))
      ]);
      setCompanyUsers(usersRes.data || []);
      setCompanyEmployees(employeesRes.data || []);
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  };

  const openEditCompany = (company) => {
    setSelectedCompany(company);
    setCompanyForm({
      name: company.name || "",
      code: company.code || "",
      plan_id: company.plan_id || "",
      billing_email: company.billing_email || "",
      status: company.status || "trial",
      address: company.address || "",
      phone: company.phone || "",
      max_employees: company.max_employees || 0,
      max_admins: company.max_admins || 0,
      trial_ends_at: company.trial_ends_at || "",
      subscription_ends_at: company.subscription_ends_at || ""
    });
    setShowEditCompanyDialog(true);
  };

  const saveCompany = async () => {
    if (!selectedCompany) return;
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${ADMIN_API}/admin/companies/${selectedCompany.id}`, companyForm, { headers });
      toast.success("企業情報を更新しました");
      setShowEditCompanyDialog(false);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const openAddCompanyDialog = () => {
    setNewCompanyForm({
      name: "", billing_email: "", plan_id: "", admin_name: "", admin_email: "", admin_password: ""
    });
    setShowAddCompanyDialog(true);
  };

  const createCompany = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${ADMIN_API}/admin/companies`, newCompanyForm, { headers });
      toast.success("企業を作成しました");
      setShowAddCompanyDialog(false);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "企業の作成に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const { type, item } = deleteTarget;
      
      if (type === "company") {
        await axios.delete(`${ADMIN_API}/admin/companies/${item.id}`, { headers });
        toast.success("企業を削除しました");
      } else if (type === "plan") {
        await axios.delete(`${ADMIN_API}/admin/plans/${item.id}`, { headers });
        toast.success("プランを削除しました");
      } else if (type === "user") {
        await axios.delete(`${ADMIN_API}/admin/users/${item.id}`, { headers });
        toast.success("ユーザーを削除しました");
      } else if (type === "employee") {
        await axios.delete(`${ADMIN_API}/admin/employees/${item.id}`, { headers });
        toast.success("従業員を削除しました");
      }
      
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchAdminData();
      
      // Refresh company details if viewing
      if (selectedCompany && (type === "user" || type === "employee")) {
        viewCompany(selectedCompany);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "削除に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const suspendCompany = async (companyId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${ADMIN_API}/admin/companies/${companyId}/suspend`, {}, { headers });
      toast.success("企業を停止しました");
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "停止に失敗しました");
    }
  };

  const activateCompany = async (companyId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${ADMIN_API}/admin/companies/${companyId}/activate`, {}, { headers });
      toast.success("企業を有効化しました");
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "有効化に失敗しました");
    }
  };

  const cancelCompany = async (companyId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${ADMIN_API}/admin/companies/${companyId}/cancel`, {}, { headers });
      toast.success("企業を解約しました");
      fetchAdminData();
      setShowCompanyDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "解約に失敗しました");
    }
  };

  const extendTrial = async (companyId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${ADMIN_API}/admin/companies/${companyId}/extend-trial`, { days: 14 }, { headers });
      toast.success("トライアル期間を14日延長しました");
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "延長に失敗しました");
    }
  };

  // System Settings Functions
  const saveSystemSettings = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${ADMIN_API}/admin/settings`, systemSettings, { headers });
      toast.success("システム設定を保存しました");
    } catch (error) {
      toast.error(error.response?.data?.detail || "設定の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const testSmtpConnection = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${ADMIN_API}/admin/settings/test-smtp`, systemSettings, { headers });
      toast.success("SMTP接続テスト成功");
    } catch (error) {
      toast.error(error.response?.data?.detail || "SMTP接続テスト失敗");
    } finally {
      setSaving(false);
    }
  };

  const saveOperatingCompany = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${ADMIN_API}/admin/operating-company`, operatingCompany, { headers });
      toast.success("運営会社情報を保存しました");
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // User Management Functions
  const openUserDialog = (user = null) => {
    setEditingUser(user);
    if (user) {
      setUserForm({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "admin",
        is_active: user.is_active !== false
      });
    } else {
      setUserForm({
        name: "",
        email: "",
        password: "",
        role: "admin",
        is_active: true
      });
    }
    setShowUserDialog(true);
  };

  const saveUser = async () => {
    if (!selectedCompany) return;
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: userForm.name,
          role: userForm.role,
          is_active: userForm.is_active
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await axios.put(`${ADMIN_API}/admin/users/${editingUser.id}`, updateData, { headers });
        toast.success("ユーザーを更新しました");
      } else {
        // Create new user
        await axios.post(`${ADMIN_API}/admin/companies/${selectedCompany.id}/users`, userForm, { headers });
        toast.success("ユーザーを作成しました");
      }
      setShowUserDialog(false);
      viewCompany(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${ADMIN_API}/admin/impersonate/${userId}`, {}, { headers });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      toast.success(`${res.data.user.name}としてログインしました`);
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "なりすましに失敗しました");
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${ADMIN_API}/admin/notifications/mark-all-read`, {}, { headers });
      toast.success("全ての通知を既読にしました");
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      toast.error("エラーが発生しました");
    }
  };

  // View Company Attendance
  const viewCompanyAttendance = async (company) => {
    setSelectedCompany(company);
    try {
      const headers = getAuthHeaders();
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const res = await axios.get(`${ADMIN_API}/admin/companies/${company.id}/attendance?year=${year}&month=${month}`, { headers });
      setCompanyAttendance(res.data || []);
      setShowAttendanceDialog(true);
    } catch (error) {
      toast.error("勤怠データの取得に失敗しました");
    }
  };

  // Plan Actions
  const openPlanDialog = (plan = null) => {
    setEditingPlan(plan);
    if (plan) {
      setPlanForm({
        name: plan.name,
        max_users: plan.max_users,
        max_employees: plan.max_employees,
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        features: plan.features || {},
        sort_order: plan.sort_order || 0
      });
    } else {
      setPlanForm({
        name: "", max_users: 3, max_employees: 10, price_monthly: 0, price_yearly: 0,
        features: { face_auth: true, csv_export: false, print: false, email_notify: false, api_access: false, multi_admin: false },
        sort_order: 0
      });
    }
    setShowPlanDialog(true);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      if (editingPlan) {
        await axios.put(`${ADMIN_API}/admin/plans/${editingPlan.id}`, planForm, { headers });
        toast.success("プランを更新しました");
      } else {
        await axios.post(`${ADMIN_API}/admin/plans`, planForm, { headers });
        toast.success("プランを作成しました");
      }
      setShowPlanDialog(false);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = (plan) => {
    confirmDelete("plan", plan);
  };

  // Status Badge
  const getStatusBadge = (status) => {
    const colors = {
      trial: "bg-blue-600",
      active: "bg-emerald-600",
      suspended: "bg-yellow-600",
      cancelled: "bg-red-600"
    };
    return <Badge className={`${colors[status] || 'bg-slate-600'} text-white`}>{statusLabels[status] || status}</Badge>;
  };

  // Filtered companies
  const filteredCompanies = companies.filter(c => {
    const matchesSearch = !companyFilter || 
      c.name?.toLowerCase().includes(companyFilter.toLowerCase()) ||
      c.code?.toLowerCase().includes(companyFilter.toLowerCase());
    const matchesStatus = !statusFilter || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-500" />
            <div>
              <h1 className="text-xl font-bold text-white">SaaS管理コンソール</h1>
              <p className="text-sm text-white/70">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-purple-600 text-white">{admin?.role === 'super_admin' ? 'スーパー管理者' : '管理者'}</Badge>
            <Button variant="outline" onClick={handleLogout} className="text-white border-slate-600 hover:bg-slate-700">
              <LogOut className="w-4 h-4 mr-2" />
              ログアウト
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Metrics Cards */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-sm text-white/70">総企業数</p>
                    <p className="text-2xl font-bold text-white">{metrics.companies?.total || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Play className="w-8 h-8 text-emerald-400" />
                  <div>
                    <p className="text-sm text-white/70">有効企業</p>
                    <p className="text-2xl font-bold text-white">{metrics.companies?.active || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-sm text-white/70">トライアル</p>
                    <p className="text-2xl font-bold text-white">{metrics.companies?.trial || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-sm text-white/70">総ユーザー</p>
                    <p className="text-2xl font-bold text-white">{metrics.users || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Briefcase className="w-8 h-8 text-orange-400" />
                  <div>
                    <p className="text-sm text-white/70">総従業員</p>
                    <p className="text-2xl font-bold text-white">{metrics.employees || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-8 h-8 text-yellow-400" />
                  <div>
                    <p className="text-sm text-white/70">MRR</p>
                    <p className="text-2xl font-bold text-white">¥{(metrics.mrr || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 mb-6 p-1 flex-wrap">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              ダッシュボード
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <Building2 className="w-4 h-4 mr-2" />
              企業管理
            </TabsTrigger>
            <TabsTrigger value="plans" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              プラン
            </TabsTrigger>
            <TabsTrigger value="operating" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <Briefcase className="w-4 h-4 mr-2" />
              運営会社
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <FileText className="w-4 h-4 mr-2" />
              見積
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <Settings className="w-4 h-4 mr-2" />
              システム設定
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <History className="w-4 h-4 mr-2" />
              操作ログ
            </TabsTrigger>
            <TabsTrigger value="impersonation" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <UserCheck className="w-4 h-4 mr-2" />
              なりすまし
              {impersonationLogs.length > 0 && (
                <Badge className="ml-2 bg-purple-600">{impersonationLogs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white">
              <Bell className="w-4 h-4 mr-2" />
              通知
              {notifications.filter(n => !n.is_read).length > 0 && (
                <Badge className="ml-2 bg-red-600">{notifications.filter(n => !n.is_read).length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* 見積タブ */}
          <TabsContent value="quotes" className="space-y-6">
            <QuoteAdminPanel adminApi={ADMIN_API} getAuthHeaders={getAuthHeaders} plans={plans} />
          </TabsContent>

          {/* KPI Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-emerald-100 text-xs font-medium">月間売上 (MRR)</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        ¥{((metrics?.mrr || 0)).toLocaleString()}
                      </p>
                      <p className="text-emerald-200 text-xs mt-1 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        前月比 +{metrics?.mrr_growth || 0}%
                      </p>
                    </div>
                    <div className="bg-emerald-500/30 rounded-lg p-2">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-blue-100 text-xs font-medium">アクティブ企業数</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {metrics?.active_companies || 0}
                      </p>
                      <p className="text-blue-200 text-xs mt-1">
                        トライアル: {metrics?.trial_companies || 0}社
                      </p>
                    </div>
                    <div className="bg-blue-500/30 rounded-lg p-2">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-purple-100 text-xs font-medium">総従業員数</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {metrics?.total_employees || 0}
                      </p>
                      <p className="text-purple-200 text-xs mt-1">
                        企業平均: {Math.round((metrics?.total_employees || 0) / Math.max(1, (metrics?.active_companies || 1)))}名
                      </p>
                    </div>
                    <div className="bg-purple-500/30 rounded-lg p-2">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-600 to-orange-700 border-0">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-orange-100 text-xs font-medium">解約率 (Churn)</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {metrics?.churn_rate?.toFixed(1) || 0}%
                      </p>
                      <p className="text-orange-200 text-xs mt-1 flex items-center gap-1">
                        {(metrics?.churn_rate || 0) > 5 ? (
                          <><TrendingUp className="w-3 h-3" /> 注意が必要</>
                        ) : (
                          <><TrendingDown className="w-3 h-3" /> 良好</>
                        )}
                      </p>
                    </div>
                    <div className="bg-orange-500/30 rounded-lg p-2">
                      <Activity className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Metrics */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    プラン別売上
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {plans.map(plan => {
                    const planCompanies = companies.filter(c => c.plan_id === plan.id && c.status === 'active');
                    const planRevenue = planCompanies.length * (plan.monthly_price || 0);
                    const percentage = metrics?.mrr > 0 ? (planRevenue / metrics.mrr * 100) : 0;
                    return (
                      <div key={plan.id} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-300">{plan.name}</span>
                          <span className="text-white font-medium">¥{planRevenue.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500">{planCompanies.length}社 ({percentage.toFixed(1)}%)</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    アットリスク企業
                  </CardTitle>
                  <CardDescription className="text-slate-400 text-xs">
                    トライアル期限切れ間近・長期未使用
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {companies
                      .filter(c => {
                        if (c.status === 'trial' && c.trial_end_date) {
                          const daysLeft = Math.ceil((new Date(c.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24));
                          return daysLeft <= 7 && daysLeft >= 0;
                        }
                        return false;
                      })
                      .slice(0, 5)
                      .map(company => (
                        <div key={company.id} className="flex justify-between items-center p-2 bg-yellow-500/10 rounded-lg">
                          <span className="text-sm text-white">{company.name}</span>
                          <Badge className="bg-yellow-600 text-xs">
                            残{Math.ceil((new Date(company.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24))}日
                          </Badge>
                        </div>
                      ))}
                    {companies.filter(c => c.status === 'trial' && c.trial_end_date && Math.ceil((new Date(c.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 7 && Math.ceil((new Date(c.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)) >= 0).length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">アットリスク企業はありません</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    最近のアクティビティ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {logs.slice(0, 5).map((log, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                        <div>
                          <p className="text-slate-300">{log.action}</p>
                          <p className="text-slate-500">{new Date(log.timestamp).toLocaleString('ja-JP')}</p>
                        </div>
                      </div>
                    ))}
                    {logs.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">最近のアクティビティはありません</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-sm">ステータス別企業数</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-400">{companies.filter(c => c.status === 'active').length}</p>
                    <p className="text-xs text-slate-400 mt-1">有効</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <p className="text-2xl font-bold text-blue-400">{companies.filter(c => c.status === 'trial').length}</p>
                    <p className="text-xs text-slate-400 mt-1">トライアル</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-400">{companies.filter(c => c.status === 'suspended').length}</p>
                    <p className="text-xs text-slate-400 mt-1">停止中</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <p className="text-2xl font-bold text-red-400">{companies.filter(c => c.status === 'cancelled').length}</p>
                    <p className="text-xs text-slate-400 mt-1">解約</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <p className="text-2xl font-bold text-white">{companies.length}</p>
                    <p className="text-xs text-slate-400 mt-1">合計</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-4">
            <div className="flex gap-4 items-center justify-between">
              <div className="flex gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="企業名・コードで検索..."
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                  />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="ステータス" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべて</SelectItem>
                    <SelectItem value="active">有効</SelectItem>
                    <SelectItem value="trial">トライアル</SelectItem>
                    <SelectItem value="suspended">停止中</SelectItem>
                    <SelectItem value="cancelled">解約</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={openAddCompanyDialog} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                企業追加
              </Button>
            </div>

            <div className="space-y-3">
              {filteredCompanies.map((company) => (
                <Card key={company.id} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg text-white">{company.name}</h3>
                          <span className="text-sm text-white/80 font-mono">{company.code}</span>
                          {getStatusBadge(company.status)}
                        </div>
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-white/80">
                          <p><span className="text-white/50">プラン:</span> {company.plan?.name || "未設定"}</p>
                          <p><span className="text-white/50">ユーザー:</span> {company.stats?.user_count || 0}名</p>
                          <p><span className="text-white/50">従業員:</span> {company.stats?.employee_count || 0}名</p>
                          <p><span className="text-white/50">請求先:</span> {company.billing_email || "-"}</p>
                        </div>
                        {company.trial_ends_at && company.status === "trial" && (
                          <p className="mt-1 text-sm text-yellow-400">
                            トライアル期限: {new Date(company.trial_ends_at).toLocaleDateString('ja-JP')}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => viewCompany(company)} className="text-white border-slate-600" title="詳細">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditCompany(company)} className="text-blue-400 border-slate-600" title="編集">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => viewCompanyAttendance(company)} className="text-purple-400 border-slate-600" title="勤怠">
                          <Calendar className="w-4 h-4" />
                        </Button>
                        {company.status === "active" && (
                          <Button variant="outline" size="sm" onClick={() => suspendCompany(company.id)} className="text-yellow-400 border-slate-600" title="停止">
                            <Pause className="w-4 h-4" />
                          </Button>
                        )}
                        {company.status === "suspended" && (
                          <Button variant="outline" size="sm" onClick={() => activateCompany(company.id)} className="text-emerald-400 border-slate-600" title="有効化">
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        {company.status === "trial" && (
                          <Button variant="outline" size="sm" onClick={() => extendTrial(company.id)} className="text-blue-400 border-slate-600" title="延長">
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => confirmDelete("company", company)} className="text-red-400 border-slate-600" title="削除">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredCompanies.length === 0 && (
                <div className="text-center text-white/60 py-8">企業がありません</div>
              )}
            </div>
          </TabsContent>

          {/* Plans Tab */}
          <TabsContent value="plans" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => openPlanDialog()} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                新規プラン
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <Card key={plan.id} className={`bg-slate-800 border-slate-700 ${!plan.is_active ? 'opacity-50' : ''}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-white">
                      {plan.name}
                      {!plan.is_active && <Badge variant="secondary">無効</Badge>}
                    </CardTitle>
                    <CardDescription>
                      <span className="text-2xl font-bold text-white">¥{plan.price_monthly.toLocaleString()}</span>
                      <span className="text-white/70">/月</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm space-y-1 text-white/80">
                      <p>ユーザー上限: {plan.max_users}名</p>
                      <p>従業員上限: {plan.max_employees}名</p>
                      <p>年額: ¥{plan.price_yearly?.toLocaleString() || 0}</p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(plan.features || {}).filter(([_, v]) => v).map(([k]) => (
                        <Badge key={k} variant="outline" className="text-xs text-white border-slate-500">{featureLabels[k] || k}</Badge>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => openPlanDialog(plan)} className="text-white border-slate-600">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deletePlan(plan)} className="text-red-400 border-slate-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Logo Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  システムロゴ
                </CardTitle>
                <CardDescription className="text-slate-400">
                  システム全体で使用するロゴマークを設定します
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-6">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-slate-700 rounded-xl border-2 border-dashed border-slate-600 flex items-center justify-center overflow-hidden">
                      {logoUrl ? (
                        <img 
                          src={logoUrl} 
                          alt="システムロゴ" 
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="text-center text-slate-500">
                          <Image className="w-8 h-8 mx-auto mb-1" />
                          <span className="text-xs">ロゴなし</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            アップロード中...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            ロゴをアップロード
                          </>
                        )}
                      </Button>
                      
                      {logoUrl && (
                        <Button
                          variant="outline"
                          onClick={handleLogoDelete}
                          className="ml-2 border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          削除
                        </Button>
                      )}
                    </div>
                    
                    <div className="text-xs text-slate-400 space-y-1">
                      <p>• 推奨サイズ: 200x200ピクセル以上</p>
                      <p>• 対応形式: PNG, JPG, SVG</p>
                      <p>• 最大サイズ: 2MB</p>
                      <p>• 背景透過のPNGを推奨</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* General Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  一般設定
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">トライアル期間（日）</Label>
                    <Input
                      type="number"
                      value={systemSettings.trial_days}
                      onChange={(e) => setSystemSettings({ ...systemSettings, trial_days: parseInt(e.target.value) || 14 })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">デフォルトプラン</Label>
                    <Select 
                      value={systemSettings.default_plan_id || "none"} 
                      onValueChange={(val) => setSystemSettings({ ...systemSettings, default_plan_id: val === "none" ? "" : val })}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="プランを選択" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">なし（トライアル）</SelectItem>
                        {plans.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">企業コードプレフィックス</Label>
                    <Input
                      value={systemSettings.company_code_prefix}
                      onChange={(e) => setSystemSettings({ ...systemSettings, company_code_prefix: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="例: PRC"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={systemSettings.allow_registration}
                      onCheckedChange={(v) => setSystemSettings({ ...systemSettings, allow_registration: v })}
                    />
                    <Label className="text-white">新規登録を許可</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={systemSettings.require_email_verification}
                      onCheckedChange={(v) => setSystemSettings({ ...systemSettings, require_email_verification: v })}
                    />
                    <Label className="text-white">メール認証を必須</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  セキュリティ設定
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">最大ログイン試行回数</Label>
                    <Input
                      type="number"
                      value={systemSettings.max_login_attempts}
                      onChange={(e) => setSystemSettings({ ...systemSettings, max_login_attempts: parseInt(e.target.value) || 5 })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">セッション有効時間（時間）</Label>
                    <Input
                      type="number"
                      value={systemSettings.session_timeout_hours}
                      onChange={(e) => setSystemSettings({ ...systemSettings, session_timeout_hours: parseInt(e.target.value) || 24 })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">パスワード最小文字数</Label>
                    <Input
                      type="number"
                      value={systemSettings.password_min_length}
                      onChange={(e) => setSystemSettings({ ...systemSettings, password_min_length: parseInt(e.target.value) || 8 })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={systemSettings.require_strong_password}
                    onCheckedChange={(v) => setSystemSettings({ ...systemSettings, require_strong_password: v })}
                  />
                  <Label className="text-white">強力なパスワードを必須（大文字、小文字、数字を含む）</Label>
                </div>
              </CardContent>
            </Card>

            {/* Email Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  メール設定（SMTP）
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">SMTPホスト</Label>
                    <Input
                      value={systemSettings.smtp_host}
                      onChange={(e) => setSystemSettings({ ...systemSettings, smtp_host: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">SMTPポート</Label>
                    <Input
                      type="number"
                      value={systemSettings.smtp_port}
                      onChange={(e) => setSystemSettings({ ...systemSettings, smtp_port: parseInt(e.target.value) || 587 })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">SMTPユーザー</Label>
                    <Input
                      value={systemSettings.smtp_user}
                      onChange={(e) => setSystemSettings({ ...systemSettings, smtp_user: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="user@gmail.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">SMTPパスワード</Label>
                    <Input
                      type="password"
                      value={systemSettings.smtp_password}
                      onChange={(e) => setSystemSettings({ ...systemSettings, smtp_password: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">送信元メールアドレス</Label>
                    <Input
                      type="email"
                      value={systemSettings.smtp_from_email}
                      onChange={(e) => setSystemSettings({ ...systemSettings, smtp_from_email: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="noreply@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">送信者名</Label>
                    <Input
                      value={systemSettings.smtp_from_name}
                      onChange={(e) => setSystemSettings({ ...systemSettings, smtp_from_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="PRC勤怠管理"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">お問い合わせ通知先メールアドレス</Label>
                  <Input
                    type="email"
                    value={systemSettings.contact_notification_email}
                    onChange={(e) => setSystemSettings({ ...systemSettings, contact_notification_email: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="例: info@your-company.co.jp（未設定時は送信元アドレスへ通知）"
                    data-testid="contact-notification-email-input"
                  />
                  <p className="text-xs text-slate-400">公開サイトのお問い合わせフォームに送信があった際、このアドレスへ通知メールが届きます。</p>
                </div>
                <Button variant="outline" onClick={testSmtpConnection} disabled={saving} className="text-white border-slate-600">
                  <Zap className="w-4 h-4 mr-2" />
                  接続テスト
                </Button>
              </CardContent>
            </Card>

            {/* Maintenance Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  メンテナンス設定
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={systemSettings.maintenance_mode}
                    onCheckedChange={(v) => setSystemSettings({ ...systemSettings, maintenance_mode: v })}
                  />
                  <Label className="text-white">メンテナンスモードを有効にする</Label>
                  {systemSettings.maintenance_mode && (
                    <Badge className="bg-yellow-600 text-white">メンテナンス中</Badge>
                  )}
                </div>
                {systemSettings.maintenance_mode && (
                  <div className="space-y-2">
                    <Label className="text-white">メンテナンスメッセージ</Label>
                    <Input
                      value={systemSettings.maintenance_message}
                      onChange={(e) => setSystemSettings({ ...systemSettings, maintenance_message: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="現在メンテナンス中です。しばらくお待ちください。"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={saveSystemSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                設定を保存
              </Button>
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 items-center flex-wrap">
              <Select value={logActionFilter || "all"} onValueChange={(val) => setLogActionFilter(val === "all" ? "" : val)}>
                <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white" data-testid="log-action-filter">
                  <SelectValue placeholder="アクション種別" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全てのアクション</SelectItem>
                  <SelectItem value="login">ログイン</SelectItem>
                  <SelectItem value="create_company">企業作成</SelectItem>
                  <SelectItem value="update_company">企業更新</SelectItem>
                  <SelectItem value="suspend_company">企業停止</SelectItem>
                  <SelectItem value="activate_company">企業有効化</SelectItem>
                  <SelectItem value="impersonate_user">なりすまし</SelectItem>
                  <SelectItem value="create_plan">プラン作成</SelectItem>
                  <SelectItem value="update_plan">プラン更新</SelectItem>
                  <SelectItem value="update_settings">設定更新</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-white/60">
                {logs.filter(l => !logActionFilter || l.action === logActionFilter).length}件
              </span>
            </div>

            <div className="space-y-2">
              {logs
                .filter(l => !logActionFilter || l.action === logActionFilter)
                .map((log) => (
                <Card key={log.id} className="bg-slate-800 border-slate-700" data-testid={`log-item-${log.id}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs ${
                            log.action.includes('create') ? 'bg-emerald-600' :
                            log.action.includes('update') ? 'bg-blue-600' :
                            log.action.includes('delete') ? 'bg-red-600' :
                            log.action.includes('suspend') ? 'bg-yellow-600' :
                            log.action.includes('impersonate') ? 'bg-purple-600' :
                            'bg-slate-600'
                          }`}>
                            {log.action}
                          </Badge>
                          <span className="text-xs text-white/50">{log.target_type}</span>
                        </div>
                        <p className="text-sm text-white/70">
                          実行者: {log.admin_email}
                        </p>
                        {log.target_id && (
                          <p className="text-xs text-white/50">
                            対象ID: {log.target_id.slice(0, 12)}...
                          </p>
                        )}
                        {log.ip_address && (
                          <p className="text-xs text-white/40">
                            IP: {log.ip_address}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-white/60 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('ja-JP')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {logs.filter(l => !logActionFilter || l.action === logActionFilter).length === 0 && (
                <div className="text-center text-white/60 py-8">ログがありません</div>
              )}
            </div>
          </TabsContent>

          {/* Impersonation Logs Tab */}
          <TabsContent value="impersonation" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-purple-400" />
                  なりすましログ
                </CardTitle>
                <CardDescription className="text-white/70">
                  管理者がテナントユーザーとしてログインした履歴を表示します
                </CardDescription>
              </CardHeader>
              <CardContent>
                {impersonationLogs.length === 0 ? (
                  <div className="text-center text-white/60 py-8">
                    <UserCheck className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>なりすましログはありません</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {impersonationLogs.map((log) => (
                      <div key={log.id} className="p-4 bg-slate-700 rounded-lg" data-testid={`impersonation-log-${log.id}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-purple-600">なりすまし</Badge>
                              <span className="text-white font-medium">{log.admin_name || log.admin_email}</span>
                              <ChevronRight className="w-4 h-4 text-white/50" />
                              <span className="text-emerald-400 font-medium">{log.target_user_name || log.target_user_email}</span>
                            </div>
                            <div className="text-sm text-white/70 space-y-1">
                              <p>
                                <span className="text-white/50">対象企業:</span> {log.target_company_name} ({log.target_company_code})
                              </p>
                              <p>
                                <span className="text-white/50">管理者メール:</span> {log.admin_email}
                              </p>
                              <p>
                                <span className="text-white/50">対象ユーザーメール:</span> {log.target_user_email}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-white/60">
                              {new Date(log.started_at).toLocaleString('ja-JP')}
                            </p>
                            {log.ended_at && (
                              <p className="text-xs text-emerald-400 mt-1">
                                終了: {new Date(log.ended_at).toLocaleString('ja-JP')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4">
            {/* Filters and Actions */}
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-4 items-center">
                <Select value={notificationTypeFilter || "all"} onValueChange={(val) => setNotificationTypeFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white" data-testid="notification-type-filter">
                    <SelectValue placeholder="通知種別" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全ての通知</SelectItem>
                    <SelectItem value="company_created">企業作成</SelectItem>
                    <SelectItem value="payment_failed">支払い失敗</SelectItem>
                    <SelectItem value="trial_ending">トライアル期限</SelectItem>
                    <SelectItem value="limit_warning">上限警告</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showReadNotifications}
                    onCheckedChange={setShowReadNotifications}
                  />
                  <Label className="text-white text-sm">既読を表示</Label>
                </div>
              </div>
              {notifications.filter(n => !n.is_read).length > 0 && (
                <Button variant="outline" size="sm" onClick={markAllNotificationsRead} className="text-white border-slate-600" data-testid="mark-all-read-btn">
                  <Check className="w-4 h-4 mr-2" />
                  全て既読
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {notifications
                .filter(n => (!notificationTypeFilter || n.type === notificationTypeFilter) && (showReadNotifications || !n.is_read))
                .map((notif) => (
                <Card key={notif.id} className={`bg-slate-800 border-slate-700 ${!notif.is_read ? 'border-l-4 border-l-blue-500' : ''}`} data-testid={`notification-${notif.id}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs ${
                            notif.type === 'company_created' ? 'bg-emerald-600' :
                            notif.type === 'payment_failed' ? 'bg-red-600' :
                            notif.type === 'trial_ending' ? 'bg-yellow-600' :
                            notif.type === 'limit_warning' ? 'bg-orange-600' :
                            'bg-slate-600'
                          }`}>
                            {notif.type}
                          </Badge>
                          {!notif.is_read && <Badge className="bg-blue-600 text-xs">未読</Badge>}
                        </div>
                        <p className={`text-white ${!notif.is_read ? 'font-medium' : ''}`}>{notif.message}</p>
                      </div>
                      <span className="text-xs text-white/60 whitespace-nowrap">
                        {new Date(notif.created_at).toLocaleString('ja-JP')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {notifications.filter(n => (!notificationTypeFilter || n.type === notificationTypeFilter) && (showReadNotifications || !n.is_read)).length === 0 && (
                <div className="text-center text-white/60 py-8">通知がありません</div>
              )}
            </div>
          </TabsContent>

          {/* Operating Company Tab */}
          <TabsContent value="operating" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  運営会社情報
                </CardTitle>
                <CardDescription className="text-white/70">
                  SaaSサービスを運営する会社の基本情報を設定します
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">会社名</Label>
                    <Input
                      value={operatingCompany.company_name}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, company_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="株式会社〇〇"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">会社名（カナ）</Label>
                    <Input
                      value={operatingCompany.company_name_kana}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, company_name_kana: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="カブシキガイシャマルマル"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">代表者名</Label>
                    <Input
                      value={operatingCompany.representative_name}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, representative_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">郵便番号</Label>
                    <Input
                      value={operatingCompany.postal_code}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, postal_code: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="123-4567"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">住所</Label>
                  <Input
                    value={operatingCompany.address}
                    onChange={(e) => setOperatingCompany({ ...operatingCompany, address: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="東京都〇〇区〇〇町1-2-3"
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">電話番号</Label>
                    <Input
                      value={operatingCompany.phone}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, phone: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="03-1234-5678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">FAX番号</Label>
                    <Input
                      value={operatingCompany.fax}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, fax: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="03-1234-5679"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">メールアドレス</Label>
                    <Input
                      type="email"
                      value={operatingCompany.email}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, email: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="info@example.com"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Webサイト</Label>
                    <Input
                      value={operatingCompany.website}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, website: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">インボイス登録番号</Label>
                    <Input
                      value={operatingCompany.invoice_number}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, invoice_number: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="T1234567890123"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Account Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  振込先口座情報
                </CardTitle>
                <CardDescription className="text-white/70">
                  請求書や支払い通知に表示される口座情報を設定します
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">銀行名</Label>
                    <Input
                      value={operatingCompany.bank_name}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, bank_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="〇〇銀行"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">支店名</Label>
                    <Input
                      value={operatingCompany.bank_branch}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, bank_branch: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="〇〇支店"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">口座種類</Label>
                    <Select 
                      value={operatingCompany.bank_account_type} 
                      onValueChange={(val) => setOperatingCompany({ ...operatingCompany, bank_account_type: val })}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="口座種類を選択" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="普通">普通</SelectItem>
                        <SelectItem value="当座">当座</SelectItem>
                        <SelectItem value="貯蓄">貯蓄</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">口座番号</Label>
                    <Input
                      value={operatingCompany.bank_account_number}
                      onChange={(e) => setOperatingCompany({ ...operatingCompany, bank_account_number: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="1234567"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">口座名義</Label>
                  <Input
                    value={operatingCompany.bank_account_holder}
                    onChange={(e) => setOperatingCompany({ ...operatingCompany, bank_account_holder: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="カ）マルマル"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={saveOperatingCompany} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                運営会社情報を保存
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Company Detail Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="max-w-3xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              企業詳細: {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedCompany && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-700/50 rounded-lg">
                <div>
                  <Label className="text-white/70 text-xs">企業名</Label>
                  <p className="font-medium text-white">{selectedCompany.name}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">企業コード</Label>
                  <p className="font-mono text-white">{selectedCompany.code}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">ステータス</Label>
                  <p>{getStatusBadge(selectedCompany.status)}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">プラン</Label>
                  <p className="text-white">{selectedCompany.plan?.name || "未設定"}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">請求先メール</Label>
                  <p className="text-white">{selectedCompany.billing_email || "-"}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-xs">登録日</Label>
                  <p className="text-white">{new Date(selectedCompany.created_at).toLocaleDateString('ja-JP')}</p>
                </div>
              </div>

              {/* Users Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <User className="w-4 h-4" />
                    管理ユーザー ({companyUsers.length}名)
                  </h4>
                  <Button size="sm" onClick={() => openUserDialog()} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Plus className="w-4 h-4 mr-1" />
                    ユーザー追加
                  </Button>
                </div>
                <div className="space-y-2">
                  {companyUsers.map((user) => (
                    <div key={user.id} className="flex justify-between items-center p-3 bg-slate-700 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{user.name}</p>
                          {user.is_active === false && <Badge className="bg-red-600 text-white text-xs">無効</Badge>}
                          <Badge className={user.role === 'super_admin' ? 'bg-purple-600' : 'bg-blue-600'} variant="outline" className="text-xs text-white border-slate-500">
                            {user.role === 'super_admin' ? 'スーパー管理者' : user.role === 'admin' ? '管理者' : user.role || '管理者'}
                          </Badge>
                        </div>
                        <p className="text-sm text-white/70">{user.email}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openUserDialog(user)} className="text-blue-400 border-slate-600" title="編集" data-testid={`edit-user-btn-${user.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => impersonateUser(user.id)} className="text-emerald-400 border-slate-600" title="ログイン" data-testid={`impersonate-btn-${user.id}`}>
                          <UserCheck className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => confirmDelete("user", user)} className="text-red-400 border-slate-600" title="削除" data-testid={`delete-user-btn-${user.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {companyUsers.length === 0 && (
                    <p className="text-white/60 text-sm">ユーザーがいません</p>
                  )}
                </div>
              </div>

              {/* Employees Section */}
              <div>
                <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  従業員 ({companyEmployees.length}名)
                </h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {companyEmployees.slice(0, 10).map((emp) => (
                    <div key={emp.id} className="flex justify-between items-center p-2 bg-slate-700 rounded">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center text-xs">
                          {emp.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{emp.name}</p>
                          <p className="text-xs text-white/60">{emp.employee_code} | {emp.department || "-"}</p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => confirmDelete("employee", emp)} className="text-red-400 border-slate-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {companyEmployees.length > 10 && (
                    <p className="text-white/60 text-sm text-center">他 {companyEmployees.length - 10}名...</p>
                  )}
                  {companyEmployees.length === 0 && (
                    <p className="text-white/60 text-sm">従業員がいません</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-slate-700">
                <Button variant="outline" onClick={() => openEditCompany(selectedCompany)} className="text-blue-400 border-slate-600">
                  <Edit className="w-4 h-4 mr-2" />
                  編集
                </Button>
                {selectedCompany.status === "active" && (
                  <Button variant="outline" onClick={() => { suspendCompany(selectedCompany.id); setShowCompanyDialog(false); }} className="text-yellow-400 border-slate-600">
                    <Pause className="w-4 h-4 mr-2" />
                    停止
                  </Button>
                )}
                {selectedCompany.status === "suspended" && (
                  <Button variant="outline" onClick={() => { activateCompany(selectedCompany.id); setShowCompanyDialog(false); }} className="text-emerald-400 border-slate-600">
                    <Play className="w-4 h-4 mr-2" />
                    有効化
                  </Button>
                )}
                {selectedCompany.status !== "cancelled" && (
                  <Button variant="outline" onClick={() => cancelCompany(selectedCompany.id)} className="text-red-400 border-slate-600">
                    <Ban className="w-4 h-4 mr-2" />
                    解約
                  </Button>
                )}
                <Button variant="outline" onClick={() => confirmDelete("company", selectedCompany)} className="text-red-400 border-slate-600 ml-auto">
                  <Trash2 className="w-4 h-4 mr-2" />
                  完全削除
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showEditCompanyDialog} onOpenChange={setShowEditCompanyDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">企業情報の編集</DialogTitle>
            <DialogDescription className="text-white/70">
              企業の詳細情報を編集します
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
              <h4 className="font-medium text-white text-sm">基本情報</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white text-sm">企業名 *</Label>
                  <Input
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white text-sm">企業コード *</Label>
                  <Input
                    value={companyForm.code}
                    onChange={(e) => setCompanyForm({ ...companyForm, code: e.target.value.toUpperCase() })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white text-sm">住所</Label>
                <Input
                  value={companyForm.address || ""}
                  onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="東京都〇〇区..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white text-sm">電話番号</Label>
                  <Input
                    value={companyForm.phone || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="03-xxxx-xxxx"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white text-sm">請求先メール</Label>
                  <Input
                    type="email"
                    value={companyForm.billing_email || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, billing_email: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Contract Info */}
            <div className="p-3 bg-slate-700/50 rounded-lg space-y-3">
              <h4 className="font-medium text-white text-sm">契約情報</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white text-sm">プラン</Label>
                  <Select value={companyForm.plan_id} onValueChange={(val) => setCompanyForm({ ...companyForm, plan_id: val })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="プランを選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white text-sm">ステータス</Label>
                  <Select value={companyForm.status} onValueChange={(val) => setCompanyForm({ ...companyForm, status: val })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">トライアル</SelectItem>
                      <SelectItem value="active">有効</SelectItem>
                      <SelectItem value="suspended">停止中</SelectItem>
                      <SelectItem value="cancelled">解約</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white text-sm">従業員上限</Label>
                  <Input
                    type="number"
                    value={companyForm.max_employees || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, max_employees: parseInt(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="10"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white text-sm">管理者上限</Label>
                  <Input
                    type="number"
                    value={companyForm.max_admins || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, max_admins: parseInt(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="3"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-white text-sm">トライアル終了日</Label>
                  <Input
                    type="date"
                    value={companyForm.trial_ends_at ? companyForm.trial_ends_at.split("T")[0] : ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, trial_ends_at: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white text-sm">契約更新日</Label>
                  <Input
                    type="date"
                    value={companyForm.subscription_ends_at ? companyForm.subscription_ends_at.split("T")[0] : ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, subscription_ends_at: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEditCompanyDialog(false)} className="flex-1 text-white border-slate-600">
                キャンセル
              </Button>
              <Button onClick={saveCompany} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
        <DialogContent className="max-w-4xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              勤怠データ: {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {companyAttendance.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-2 text-white/70">日付</th>
                      <th className="text-left p-2 text-white/70">従業員</th>
                      <th className="text-left p-2 text-white/70">出勤</th>
                      <th className="text-left p-2 text-white/70">退勤</th>
                      <th className="text-left p-2 text-white/70">労働時間</th>
                      <th className="text-left p-2 text-white/70">残業</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyAttendance.slice(0, 50).map((record) => (
                      <tr key={record.id} className="border-b border-slate-700/50">
                        <td className="p-2 text-white">{record.date}</td>
                        <td className="p-2 text-white">{record.employee_name}</td>
                        <td className="p-2 text-emerald-400">{record.clock_in || "-"}</td>
                        <td className="p-2 text-blue-400">{record.clock_out || "-"}</td>
                        <td className="p-2 text-white">{record.work_hours?.toFixed(1) || "-"}h</td>
                        <td className="p-2 text-yellow-400">{record.overtime_hours?.toFixed(1) || "0"}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-white/60 py-8">勤怠データがありません</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{editingPlan ? "プラン編集" : "新規プラン"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">プラン名</Label>
              <Input
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">月額（円）</Label>
                <Input
                  type="number"
                  value={planForm.price_monthly}
                  onChange={(e) => setPlanForm({ ...planForm, price_monthly: parseInt(e.target.value) || 0 })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">年額（円）</Label>
                <Input
                  type="number"
                  value={planForm.price_yearly}
                  onChange={(e) => setPlanForm({ ...planForm, price_yearly: parseInt(e.target.value) || 0 })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">ユーザー上限</Label>
                <Input
                  type="number"
                  value={planForm.max_users}
                  onChange={(e) => setPlanForm({ ...planForm, max_users: parseInt(e.target.value) || 1 })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">従業員上限</Label>
                <Input
                  type="number"
                  value={planForm.max_employees}
                  onChange={(e) => setPlanForm({ ...planForm, max_employees: parseInt(e.target.value) || 1 })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white">機能</Label>
              <div className="space-y-2">
                {Object.entries(featureLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <Switch
                      checked={planForm.features[key] || false}
                      onCheckedChange={(v) => setPlanForm({
                        ...planForm,
                        features: { ...planForm.features, [key]: v }
                      })}
                    />
                    <span className="text-sm text-white">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPlanDialog(false)} className="flex-1 text-white border-slate-600">
                キャンセル
              </Button>
              <Button onClick={savePlan} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              削除の確認
            </DialogTitle>
            <DialogDescription className="text-white/70">
              {deleteTarget?.type === "company" && `企業「${deleteTarget?.item?.name}」を完全に削除しますか？関連するすべてのデータが失われます。`}
              {deleteTarget?.type === "plan" && `プラン「${deleteTarget?.item?.name}」を削除しますか？`}
              {deleteTarget?.type === "user" && `ユーザー「${deleteTarget?.item?.name}」を削除しますか？`}
              {deleteTarget?.type === "employee" && `従業員「${deleteTarget?.item?.name}」を削除しますか？`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1 text-white border-slate-600">
              キャンセル
            </Button>
            <Button onClick={executeDelete} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "削除する"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Edit/Create Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUser ? "ユーザー編集" : "新規ユーザー追加"}
            </DialogTitle>
            <DialogDescription className="text-white/70">
              {selectedCompany?.name}のユーザー管理
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">名前 *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="山田 太郎"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">メールアドレス *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="user@example.com"
                disabled={!!editingUser}
              />
              {editingUser && <p className="text-xs text-white/50">メールアドレスは変更できません</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-white">{editingUser ? "新しいパスワード（変更する場合のみ）" : "パスワード *"}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder={editingUser ? "変更しない場合は空欄" : "パスワードを入力"}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">権限</Label>
              <Select value={userForm.role} onValueChange={(val) => setUserForm({ ...userForm, role: val })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">管理者</SelectItem>
                  <SelectItem value="super_admin">スーパー管理者</SelectItem>
                  <SelectItem value="viewer">閲覧者</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={userForm.is_active}
                onCheckedChange={(v) => setUserForm({ ...userForm, is_active: v })}
              />
              <Label className="text-white">アカウント有効</Label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowUserDialog(false)} className="flex-1 text-white border-slate-600">
                キャンセル
              </Button>
              <Button 
                onClick={saveUser} 
                disabled={saving || !userForm.name || !userForm.email || (!editingUser && !userForm.password)} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingUser ? "更新" : "作成")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={showAddCompanyDialog} onOpenChange={setShowAddCompanyDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              新規企業追加
            </DialogTitle>
            <DialogDescription className="text-white/70">
              新しい企業とその管理者アカウントを作成します
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <h4 className="font-medium text-white mb-3">企業情報</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">企業名 *</Label>
                  <Input
                    value={newCompanyForm.name}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">請求先メールアドレス</Label>
                  <Input
                    type="email"
                    value={newCompanyForm.billing_email}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, billing_email: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="billing@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">プラン</Label>
                  <Select 
                    value={newCompanyForm.plan_id || "none"} 
                    onValueChange={(val) => setNewCompanyForm({ ...newCompanyForm, plan_id: val === "none" ? "" : val })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="プランを選択" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">トライアル（14日間）</SelectItem>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>{plan.name} - ¥{plan.price_monthly.toLocaleString()}/月</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <h4 className="font-medium text-white mb-3">管理者アカウント</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">管理者名 *</Label>
                  <Input
                    value={newCompanyForm.admin_name}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, admin_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="山田 太郎"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">管理者メールアドレス *</Label>
                  <Input
                    type="email"
                    value={newCompanyForm.admin_email}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, admin_email: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="admin@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">パスワード *</Label>
                  <Input
                    type="password"
                    value={newCompanyForm.admin_password}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, admin_password: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="8文字以上"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">権限</Label>
                  <Select value={newCompanyForm.admin_role} onValueChange={(val) => setNewCompanyForm({ ...newCompanyForm, admin_role: val })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="admin-role-select">
                      <SelectValue placeholder="権限を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">管理者</SelectItem>
                      <SelectItem value="manager">マネージャー</SelectItem>
                      <SelectItem value="viewer">閲覧のみ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddCompanyDialog(false)} className="flex-1 text-white border-slate-600">
                キャンセル
              </Button>
              <Button 
                onClick={createCompany} 
                disabled={saving || !newCompanyForm.name || !newCompanyForm.admin_name || !newCompanyForm.admin_email || !newCompanyForm.admin_password} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "企業を作成"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SaaSAdminPage;
